import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import moment from 'moment';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import ErrorDisplay from './ErrorDisplay';
import DocumentViewerModal from './DocumentViewerModal';
import { roundUpToQuarter } from '../utils/priceRounding';

export default function MandatoryCarInsuranceModal({ isOpen, onClose, onSubmit, initialValues, clientId }) {
  const { t } = useLanguage();
  const { user } = useAuth();

  // State for car data
  const [carMakes, setCarMakes] = useState([]);
  const [carModels, setCarModels] = useState([]);
  const [loadingMakes, setLoadingMakes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  
  // Error and submission state
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [draftPolicy, setDraftPolicy] = useState(null);

  // Generate years from current year back to 1990
  const currentYear = new Date().getFullYear();
  const carYears = Array.from({ length: currentYear - 1989 }, (_, i) => currentYear - i);

  // Mandatory insurance options with translations
  const mandatoryInsuranceOptions = [
    {
      id: 'personal_vehicle',
      name: t('policy.personalVehicle'),
      subOptions: [
        { id: 'less_than_16_hp', name: t('policy.lessThan16Hp'), price: 44 },
        { id: '16_to_30_hp', name: t('policy.16To30Hp'), price: 50 },
        { id: 'more_than_30_hp', name: t('policy.moreThan30Hp'), price: null, needsHpCalculation: true },
        { id: 'per_passenger', name: t('policy.perPassenger'), price: 5, needsPassengerCount: true }
      ]
    },
    {
      id: 'advanced_vehicle',
      name: t('policy.advancedVehicle'),
      subOptions: [
        { id: 'attached_to_private', name: t('policy.attachedToPrivate'), price: 30 }
      ]
    },
    {
      id: 'public_passenger_car_city',
      name: t('policy.publicPassengerCarCity'),
      subOptions: [
        { 
          id: 'passenger_based_pricing', 
          name: t('policy.upTo5Passengers'), 
          price: 64, 
          basePassengers: 5, 
          excessRate: 10,
          needsAuthorizedPassengers: true 
        }
      ]
    },
    {
      id: 'public_car_outside_city',
      name: t('policy.publicCarOutsideCity'),
      subOptions: [
        { 
          id: 'passenger_based_pricing', 
          name: t('policy.upTo5PassengersOutside'), 
          price: 44, 
          basePassengers: 5, 
          excessRate: 15,
          needsAuthorizedPassengers: true 
        }
      ]
    },
    {
      id: 'bus_transport',
      name: t('policy.busTransport'),
      subOptions: [
        { 
          id: 'passenger_based_pricing', 
          name: t('policy.upTo14Passengers'), 
          price: 84, 
          basePassengers: 14, 
          excessRate: 8,
          needsAuthorizedPassengers: true 
        }
      ]
    },
    {
      id: 'field_towed_vehicles',
      name: t('policy.fieldTowedVehicles'),
      subOptions: []
    },
    {
      id: 'cargo_transport',
      name: t('policy.cargoTransport'),
      subOptions: [
        { 
          id: 'weight_based_pricing', 
          name: t('policy.upTo2Tons'), 
          price: 50, 
          baseWeight: 2, 
          excessRate: 8,
          needsVehicleWeight: true 
        }
      ]
    },
    {
      id: 'tractor_vehicles',
      name: t('policy.tractorVehicles'),
      subOptions: [
        { id: 'agricultural', name: t('policy.agricultural'), price: 16 },
        { id: 'industrial', name: t('policy.industrial'), price: 34 }
      ]
    },
    {
      id: 'trailer_vehicles',
      name: t('policy.trailerVehicles'),
      subOptions: [
        { 
          id: 'weight_based_pricing', 
          name: t('policy.upTo1Ton'), 
          price: 8, 
          baseWeight: 1, 
          excessRate: 8,
          needsVehicleWeight: true 
        }
      ]
    },
    {
      id: 'motorcycle',
      name: t('policy.motorcycle'),
      subOptions: [
        { id: 'without_accessories', name: t('policy.withoutAccessories'), price: 18 },
        { id: 'with_accessories', name: t('policy.withAccessories'), price: 24 }
      ]
    },
    { id: 'ambulance', name: t('policy.ambulance'), price: 50, subOptions: [] },
    { id: 'mortuary_vehicle', name: t('policy.mortuaryVehicle'), price: 24, subOptions: [] },
    { id: 'driving_school_cars', name: t('policy.drivingSchoolCars'), price: 58, subOptions: [] },
    { id: 'commercially_licensed_cars', name: t('policy.commerciallyLicensedCars'), price: 100, subOptions: [] }
  ];

  // Timeframe options for premium calculation
  const timeframeOptions = [
    { id: 'up_to_15_days', name: t('timeframe.upTo15Days'), percentage: 10, maxDays: 15 },
    { id: '16_to_30_days', name: t('timeframe.16To30Days'), percentage: 20, maxDays: 30 },
    { id: '31_to_60_days', name: t('timeframe.31To60Days'), percentage: 30, maxDays: 60 },
    { id: '61_to_90_days', name: t('timeframe.61To90Days'), percentage: 40, maxDays: 90 },
    { id: '91_days_or_more', name: t('timeframe.91DaysOrMore'), percentage: 100, maxDays: 365 }
  ];

  const [formData, setFormData] = useState({
    policyType: 'Mandatory Car Insurance',
    mandatoryInsuranceType: '',
    mandatoryInsuranceSubType: '',
    vehicleHorsepower: '',
    passengerCount: '',
    authorizedPassengers: '',
    vehicleWeight: '',
    basePremium: '',
    timeframeSelection: '91_days_or_more', // Default to 1 year
    timeFramePercentage: 100,
    premium: '',
    tax: 0,
    supervisionFees: 0,
    stampDuty: 1,
    issueFees: 3,
    totalPremium: '',
    limitOfIndemnity: 0, // Always 0 for mandatory car insurance
    numberOfDays: 365,
    startDate: moment().format('YYYY-MM-DD'),
    startTime: '12:00', // Default time for mandatory car insurance
    endDate: moment().add(364, 'days').format('YYYY-MM-DD'), // 365 days total (start day + 364 more)
    paymentStatus: 'unpaid',
    paymentMethod: '',
    notes: '',
    // Car insurance fields
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehiclePlate: '',
    chassisNumber: '',
    designatedDriver: '',
    designatedDriverLicense: '',
    carColour: '',
    // Personal Information (embedded in policy)
    firstName: '',
    fatherName: '',
    grandfatherName: '',
    lastName: '',
    dateOfBirth: '',
    address: '',
    nationalId: '',
    mobile: '',
    email: '',
    nationality: '',
    passportNumber: '',
    licenseNumber: '',
    occupation: '',
    clientNumber: '',
  });

  // API functions
  const fetchCarMakes = async () => {
    setLoadingMakes(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/car-data/makes?active=true', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarMakes(response.data);
    } catch (error) {
      console.error('Error fetching car makes:', error);
    } finally {
      setLoadingMakes(false);
    }
  };

  const fetchCarModels = async (makeId) => {
    if (!makeId) {
      setCarModels([]);
      return;
    }
    
    setLoadingModels(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/car-data/makes/${makeId}/models?active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarModels(response.data);
    } catch (error) {
      console.error('Error fetching car models:', error);
      setCarModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  // Calculate base premium based on selection
  const calculateBasePremium = () => {
    const selectedOption = mandatoryInsuranceOptions.find(opt => opt.id === formData.mandatoryInsuranceType);
    if (!selectedOption) return 0;

    // If option has direct price (ambulance, mortuary, etc.)
    if (selectedOption.price) {
      return selectedOption.price;
    }

    // If option has sub-options
    if (selectedOption.subOptions.length > 0) {
      const subOption = selectedOption.subOptions.find(sub => sub.id === formData.mandatoryInsuranceSubType);
      if (!subOption) return 0;

      let basePrice = subOption.price || 0;

      // Handle special calculations
      if (subOption.needsHpCalculation && formData.vehicleHorsepower > 30) {
        // For HP > 30, calculate automatically based on horsepower
        const hp = parseFloat(formData.vehicleHorsepower) || 0;
        // Formula: 50 LYD base + 4 LYD per HP above 30
        basePrice = 50 + ((hp - 30) * 4);
      } else if (subOption.needsPassengerCount && formData.passengerCount) {
        basePrice = subOption.price * parseInt(formData.passengerCount);
      } else if (subOption.needsAuthorizedPassengers && formData.authorizedPassengers) {
        // Automatic excess passenger calculation
        const totalPassengers = parseInt(formData.authorizedPassengers);
        const basePassengers = subOption.basePassengers || 5; // Default to 5 if not specified
        const excessPassengers = Math.max(0, totalPassengers - basePassengers);
        basePrice = subOption.price + (excessPassengers * subOption.excessRate);
      } else if (subOption.needsVehicleWeight && formData.vehicleWeight) {
        // Automatic excess weight calculation
        const totalWeight = parseFloat(formData.vehicleWeight);
        const baseWeight = subOption.baseWeight || 1; // Default to 1 if not specified
        const excessWeight = Math.max(0, totalWeight - baseWeight);
        basePrice = subOption.price + (Math.ceil(excessWeight) * subOption.excessRate);
      }

      // For personal vehicle options, always add 5 LYD per passenger
      if (formData.mandatoryInsuranceType === 'personal_vehicle' && formData.passengerCount) {
        const passengerFee = parseInt(formData.passengerCount) * 5;
        basePrice += passengerFee;
      }

      return basePrice;
    }

    return 0;
  };

  // Calculate time frame percentage based on actual number of days
  const calculateTimeFramePercentage = () => {
    const days = parseInt(formData.numberOfDays);
    if (days <= 15) return 10;
    if (days <= 30) return 20;
    if (days <= 60) return 30; // 1-2 months
    if (days <= 90) return 40; // 2-3 months
    return 100; // 1 year or more
  };

 

  // Load car makes when modal opens and clear error
  useEffect(() => {
    if (isOpen) {
      setError(null);
      fetchCarMakes();
    }
  }, [isOpen]);

  // Handle initialValues for editing existing policies
  useEffect(() => {
    if (initialValues && isOpen) {
      const today = moment().format('YYYY-MM-DD');
      const existingStartDate = initialValues.startDate;
      
      // Check if existing start date is in the past and update it to today if needed
      const adjustedStartDate = existingStartDate && existingStartDate < today ? today : existingStartDate;
      const adjustedEndDate = adjustedStartDate && initialValues.numberOfDays 
        ? moment(adjustedStartDate).add(initialValues.numberOfDays - 1, 'days').format('YYYY-MM-DD')
        : initialValues.endDate;
      
      setFormData(prev => ({
        ...prev,
        ...initialValues,
        limitOfIndemnity: 0, // Always ensure limit of indemnity is 0 for mandatory car insurance
        policyType: 'Mandatory Car Insurance',
        timeframeSelection: initialValues.timeframeSelection || '91_days_or_more', // Default if not present
        startDate: adjustedStartDate || today,
        endDate: adjustedEndDate || moment(today).add(364, 'days').format('YYYY-MM-DD'),
      }));
      
      // If there's a vehicle make, fetch its models
      if (initialValues.vehicleMake) {
        const selectedMake = carMakes.find(make => make.name === initialValues.vehicleMake);
        if (selectedMake) {
          fetchCarModels(selectedMake.id);
        }
      }
    } else if (isOpen && !initialValues) {
      // Reset form when opening for new policy
      setFormData({
        policyType: 'Mandatory Car Insurance',
        mandatoryInsuranceType: '',
        mandatoryInsuranceSubType: '',
        vehicleHorsepower: '',
        passengerCount: '',
        authorizedPassengers: '',
        vehicleWeight: '',
        basePremium: '',
        timeframeSelection: '91_days_or_more',
        timeFramePercentage: 100,
        premium: '',
            tax: 0,
    supervisionFees: 0,
    stampDuty: 1,
    issueFees: 3,
        totalPremium: '',
        limitOfIndemnity: 0, // Always 0 for mandatory car insurance
        numberOfDays: 365,
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().add(364, 'days').format('YYYY-MM-DD'),
        paymentStatus: 'unpaid',
        paymentMethod: '',
        notes: '',
        vehicleMake: '',
        vehicleModel: '',
        vehicleYear: '',
        vehiclePlate: '',
        chassisNumber: '',
        designatedDriver: '',
        designatedDriverLicense: '',
        carColour: '',
      });
    }
  }, [initialValues, isOpen, carMakes]);

  // Calculate premium when relevant fields change
  useEffect(() => {
    const basePremium = calculateBasePremium();
    const timeFramePercentage = calculateTimeFramePercentage();
    
    // Separate passenger fees for personal vehicles (not affected by time frame percentage)
    let passengerFees = 0;
    if (formData.mandatoryInsuranceType === 'personal_vehicle' && formData.passengerCount) {
      passengerFees = parseInt(formData.passengerCount) * 5;
    }
    
    // Calculate base premium without passenger fees for percentage calculation
    const basePremiumWithoutPassengerFees = basePremium - passengerFees;
    
    // Apply time frame percentage only to base premium (excluding passenger fees)
    const adjustedBasePremium = (basePremiumWithoutPassengerFees * timeFramePercentage) / 100;
    
    // Add passenger fees back (always full amount regardless of time frame)
    let premium = adjustedBasePremium + passengerFees;
    
    // Round premium up to nearest 0.25 increment
    premium = roundUpToQuarter(premium);
    
    // Calculate tax (1% of the ADJUSTED premium, not base premium)
    let tax = premium * 0.01;
    // Round tax up to nearest 0.25 increment
    tax = roundUpToQuarter(tax);
    
    // Calculate supervision fees (50% of tax)
    let supervisionFees = tax * 0.5;
    // Round supervision fees up to nearest 0.25 increment
    supervisionFees = roundUpToQuarter(supervisionFees);
    
    // Calculate total premium
    let totalPremium = premium + tax + supervisionFees + parseFloat(formData.stampDuty || 0) + parseFloat(formData.issueFees || 0);
    // Round total premium up to nearest 0.25 increment
    totalPremium = roundUpToQuarter(totalPremium);
    
    setFormData(prev => ({
      ...prev,
      basePremium: basePremium.toFixed(2),
      timeFramePercentage,
      premium: premium.toFixed(2),
      tax: tax.toFixed(2),
      supervisionFees: supervisionFees.toFixed(2),
      totalPremium: totalPremium.toFixed(2)
    }));
  }, [
    formData.mandatoryInsuranceType,
    formData.mandatoryInsuranceSubType,
    formData.vehicleHorsepower,
    formData.passengerCount,
    formData.authorizedPassengers,
    formData.vehicleWeight,
    formData.timeframeSelection,
    formData.numberOfDays,
    formData.stampDuty,
    formData.issueFees
  ]);

  // Update end date when start date or number of days changes
  useEffect(() => {
    if (formData.startDate && formData.numberOfDays) {
      const endDate = moment(formData.startDate).add(formData.numberOfDays - 1, 'days').format('YYYY-MM-DD');
      setFormData(prev => ({ ...prev, endDate }));
    }
  }, [formData.startDate, formData.numberOfDays]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'vehicleMake') {
      setFormData(prev => ({ ...prev, [name]: value, vehicleModel: '' }));
      if (value) {
        const selectedMake = carMakes.find(make => make.name === value);
        if (selectedMake) {
          fetchCarModels(selectedMake.id);
        }
      } else {
        setCarModels([]);
      }
    } else if (name === 'mandatoryInsuranceType') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value, 
        mandatoryInsuranceSubType: '',
        vehicleHorsepower: '',
        passengerCount: '',
        authorizedPassengers: '',
        vehicleWeight: value === 'personal_vehicle' ? '0' : ''
      }));
    } else if (name === 'timeframeSelection') {
      // When timeframe changes, reset dates and calculate new end date
      const selectedTimeframe = timeframeOptions.find(option => option.id === value);
      const today = moment().format('YYYY-MM-DD');
      const newEndDate = moment(today).add(selectedTimeframe?.maxDays - 1 || 364, 'days').format('YYYY-MM-DD');
      
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        startDate: today,
        endDate: newEndDate,
        numberOfDays: selectedTimeframe?.maxDays || 365
      }));
    } else if (name === 'startDate') {
      // Prevent backdating - ensure start date is not before today
      const today = moment().format('YYYY-MM-DD');
      const selectedDate = value;
      
      if (selectedDate < today) {
        // If trying to select a past date, reset to today
        const selectedTimeframe = timeframeOptions.find(option => option.id === formData.timeframeSelection);
        const maxDays = selectedTimeframe?.maxDays || 365;
        const newEndDate = moment(today).add(maxDays - 1, 'days').format('YYYY-MM-DD');
        setFormData(prev => ({ ...prev, startDate: today, endDate: newEndDate, numberOfDays: maxDays }));
        return;
      }
      
      // When start date changes, recalculate end date based on selected timeframe
      const selectedTimeframe = timeframeOptions.find(option => option.id === formData.timeframeSelection);
      const maxDays = selectedTimeframe?.maxDays || 365;
      const newEndDate = moment(value).add(maxDays - 1, 'days').format('YYYY-MM-DD');
      setFormData(prev => ({ ...prev, [name]: value, endDate: newEndDate, numberOfDays: maxDays }));
    } else if (name === 'endDate') {
      // When end date changes, validate against timeframe limits
      if (formData.startDate && value) {
        const start = moment(formData.startDate);
        const end = moment(value);
        const days = end.diff(start, 'days') + 1; // Include both start and end days
        
        const selectedTimeframe = timeframeOptions.find(option => option.id === formData.timeframeSelection);
        const maxDays = selectedTimeframe?.maxDays || 365;
        
        if (days > 0 && days <= maxDays) {
          setFormData(prev => ({ ...prev, [name]: value, numberOfDays: days }));
        } else if (days > maxDays) {
          // If exceeding timeframe limit, set to max allowed
          const maxEndDate = moment(formData.startDate).add(maxDays - 1, 'days').format('YYYY-MM-DD');
          setFormData(prev => ({ ...prev, endDate: maxEndDate, numberOfDays: maxDays }));
        } else {
          // If invalid date range, reset to minimum 1 day
          const newEndDate = moment(formData.startDate).format('YYYY-MM-DD');
          setFormData(prev => ({ ...prev, endDate: newEndDate, numberOfDays: 1 }));
        }
      } else {
                setFormData(prev => ({ ...prev, [name]: value }));
        }
    } else if (name === 'numberOfDays') {
      // When number of days changes, validate against timeframe and recalculate end date
      const days = parseInt(value) || 1;
      const selectedTimeframe = timeframeOptions.find(option => option.id === formData.timeframeSelection);
      const maxDays = selectedTimeframe?.maxDays || 365;
      
      // Constrain days to timeframe limits
      const constrainedDays = Math.min(Math.max(days, 1), maxDays);
      const newEndDate = moment(formData.startDate).add(constrainedDays - 1, 'days').format('YYYY-MM-DD');
      
      setFormData(prev => ({ ...prev, numberOfDays: constrainedDays, endDate: newEndDate }));
    } else if (name === 'vehicleHorsepower' && formData.mandatoryInsuranceType === 'personal_vehicle') {
      // Auto-select appropriate sub-option based on horsepower for personal vehicles
      const hp = parseFloat(value) || 0;
      let newSubType = formData.mandatoryInsuranceSubType;
      
      if (hp > 0 && hp < 16) {
        newSubType = 'less_than_16_hp';
      } else if (hp >= 16 && hp <= 30) {
        newSubType = '16_to_30_hp';
      } else if (hp > 30) {
        newSubType = 'more_than_30_hp';
      }
      
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        mandatoryInsuranceSubType: newSubType
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const prepareSubmissionData = () => {
    // Combine date and time for startDate
    const startDateTime = moment(`${formData.startDate} ${formData.startTime}`, 'YYYY-MM-DD HH:mm').toISOString();
    const endDateTime = moment(formData.endDate).endOf('day').toISOString();
    
    // Get branch and company IDs from authenticated user
    const branchId = user?.branchId;
    const companyId = user?.companyId;

    return {
      id: draftPolicy?.id || initialValues?.id,
      // Branch and company information (REQUIRED)
      branchId,
      companyId,
      // Personal Information
      firstName: formData.firstName,
      fatherName: formData.fatherName,
      grandfatherName: formData.grandfatherName,
      lastName: formData.lastName,
      dateOfBirth: formData.dateOfBirth,
      address: formData.address,
      nationalId: formData.nationalId,
      mobile: formData.mobile,
      email: formData.email,
      nationality: formData.nationality,
      passportNumber: formData.passportNumber,
      licenseNumber: formData.licenseNumber,
      occupation: formData.occupation,
      clientNumber: formData.clientNumber,
      // Policy fields
      policyType: formData.policyType,
      mandatoryInsuranceType: formData.mandatoryInsuranceType,
      mandatoryInsuranceSubType: formData.mandatoryInsuranceSubType,
      vehicleHorsepower: formData.vehicleHorsepower ? parseFloat(formData.vehicleHorsepower) : null,
      passengerCount: formData.passengerCount ? parseInt(formData.passengerCount) : null,
      authorizedPassengers: formData.authorizedPassengers ? parseInt(formData.authorizedPassengers) : null,
      vehicleWeight: formData.vehicleWeight ? parseFloat(formData.vehicleWeight) : null,
      basePremium: formData.basePremium,
      timeFramePercentage: formData.timeFramePercentage,
      limitOfIndemnity: 0, // Always 0 for mandatory car insurance
      premium: formData.premium,
      tax: formData.tax,
      supervisionFees: formData.supervisionFees,
      stampDuty: formData.stampDuty,
      issueFees: formData.issueFees,
      totalPremium: formData.totalPremium,
      numberOfDays: formData.numberOfDays,
      startDate: startDateTime,
      endDate: endDateTime,
      paymentStatus: formData.paymentStatus,
      paymentMethod: formData.paymentStatus === 'paid' ? formData.paymentMethod : null,
      notes: formData.notes,
      // Standard car fields
      carColour: formData.carColour,
      enginePower: null, // Not used for mandatory insurance
      weight: null, // Not used for mandatory insurance  
      vehicleMake: formData.vehicleMake,
      vehicleModel: formData.vehicleModel,
      vehicleYear: formData.vehicleYear,
      vehiclePlate: formData.vehiclePlate,
      chassisNumber: formData.chassisNumber,
      designatedDriver: formData.designatedDriver,
      designatedDriverLicense: formData.designatedDriverLicense,
    };
  };

  const handleSaveDraft = async () => {
    setError(null);
    setIsSavingDraft(true);
    
    try {
      const token = localStorage.getItem('token');
      const submissionData = prepareSubmissionData();
      
      const response = await api.post(
        '/api/policies/draft',
        submissionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDraftPolicy(response.data.policy);
      toast.success(t('policy.draftSaved'));
    } catch (draftError) {
      console.error('Draft save error:', draftError);
      setError(draftError);
      toast.error(t('policy.draftSaveFailed'));
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePreview = async () => {
    // Must save as draft first before preview
    if (!draftPolicy) {
      await handleSaveDraft();
    }
    setIsPreviewOpen(true);
  };

  const handleActivate = async () => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      if (!draftPolicy) {
        throw new Error('Please save as draft first');
      }
      
      if (formData.paymentStatus !== 'paid') {
        throw new Error(t('policy.paymentRequired'));
      }
      
      const token = localStorage.getItem('token');
      const startDateTime = moment(`${formData.startDate} ${formData.startTime}`, 'YYYY-MM-DD HH:mm').toISOString();
      
      const response = await api.post(
        `/api/policies/${draftPolicy.id}/activate`,
        {
          startDate: startDateTime,
          paymentMethod: formData.paymentMethod
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(t('policy.policyActivated'));
      handleClose();
      if (onSubmit) {
        onSubmit(response.data.policy);
      }
    } catch (activateError) {
      console.error('Activation error:', activateError);
      setError(activateError);
      toast.error(activateError.response?.data?.message || t('policy.activationFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent default form submission - use the 3 buttons instead
  };

  // Get selected option and sub-option
  const selectedOption = mandatoryInsuranceOptions.find(opt => opt.id === formData.mandatoryInsuranceType);
  const selectedSubOption = selectedOption?.subOptions?.find(sub => sub.id === formData.mandatoryInsuranceSubType);

  return (
    <>
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-6xl sm:w-full sm:p-6">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                      {initialValues ? t('mandatoryModal.editTitle') : t('mandatoryModal.title')}
                    </Dialog.Title>

                    {error && (
                      <ErrorDisplay 
                        error={error} 
                        onDismiss={() => setError(null)} 
                        className="mt-4 mb-4"
                      />
                    )}
                    
                    <form onSubmit={handleSubmit} className="mt-6">
                      <div className="grid grid-cols-6 gap-6">
                        
                        {/* Policy Type - Fixed */}
                        <div className="col-span-6 sm:col-span-3">
                          <label className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.policyType')}
                          </label>
                          <input
                            type="text"
                            value={t('policy.mandatoryCarInsurance')}
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                            readOnly
                          />
                        </div>

                        {/* Mandatory Insurance Type */}
                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="mandatoryInsuranceType" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.insuranceType')}
                          </label>
                          <select
                            name="mandatoryInsuranceType"
                            id="mandatoryInsuranceType"
                            value={formData.mandatoryInsuranceType}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          >
                            <option value="">{t('mandatoryModal.selectInsuranceType')}</option>
                            {mandatoryInsuranceOptions.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Sub-options */}
                        {selectedOption && selectedOption.subOptions.length > 0 && (
                          <div className="col-span-6">
                            <label htmlFor="mandatoryInsuranceSubType" className="block text-sm font-medium text-gray-700">
                              {t('mandatoryModal.subOption')}
                            </label>
                            <select
                              name="mandatoryInsuranceSubType"
                              id="mandatoryInsuranceSubType"
                              value={formData.mandatoryInsuranceSubType}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            >
                              <option value="">{t('mandatoryModal.selectSubOption')}</option>
                              {selectedOption.subOptions.map((subOption) => (
                                <option key={subOption.id} value={subOption.id}>
                                  {subOption.name} {subOption.price && `(${subOption.price} LYD)`}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Additional fields based on sub-option */}
                        {selectedSubOption?.needsHpCalculation && (
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="vehicleHorsepower" className="block text-sm font-medium text-gray-700">
                              {t('mandatoryModal.vehicleHorsepower')}
                            </label>
                            <input
                              type="number"
                              name="vehicleHorsepower"
                              id="vehicleHorsepower"
                              value={formData.vehicleHorsepower}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>
                        )}



                        {selectedSubOption?.needsPassengerCount && (
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="passengerCount" className="block text-sm font-medium text-gray-700">
                              {t('mandatoryModal.numberOfPassengers')}
                            </label>
                            <input
                              type="number"
                              name="passengerCount"
                              id="passengerCount"
                              value={formData.passengerCount}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>
                        )}

                        {selectedSubOption?.needsAuthorizedPassengers && (
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="authorizedPassengers" className="block text-sm font-medium text-gray-700">
                              {t('mandatoryModal.totalAuthorizedPassengers')}
                            </label>
                            <input
                              type="number"
                              name="authorizedPassengers"
                              id="authorizedPassengers"
                              value={formData.authorizedPassengers}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>
                        )}

                        {selectedSubOption?.needsVehicleWeight && (
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="vehicleWeight" className="block text-sm font-medium text-gray-700">
                              {t('mandatoryModal.vehicleWeightTons')}
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              name="vehicleWeight"
                              id="vehicleWeight"
                              value={formData.vehicleWeight}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>
                        )}

                        {/* Time Period */}
                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="timeframeSelection" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.timeframe')}
                          </label>
                          <select
                            name="timeframeSelection"
                            id="timeframeSelection"
                            value={formData.timeframeSelection}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          >
                            {timeframeOptions.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name} (Max {option.maxDays} days)
                              </option>
                            ))}
                          </select>
                          <p className="mt-1 text-xs text-gray-500">
                            {t('mandatoryModal.timeFrameHelp')}
                          </p>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.startDate')}
                          </label>
                          <input
                            type="date"
                            name="startDate"
                            id="startDate"
                            value={formData.startDate}
                            onChange={handleChange}
                            min={moment().format('YYYY-MM-DD')}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.startTime')}
                          </label>
                          <input
                            type="time"
                            name="startTime"
                            id="startTime"
                            value={formData.startTime}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                          <p className="mt-1 text-xs text-gray-500">{t('mandatoryModal.defaultTime')}</p>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.endDate')}
                          </label>
                          <input
                            type="date"
                            name="endDate"
                            id="endDate"
                            value={formData.endDate}
                            onChange={handleChange}
                            min={formData.startDate}
                            max={(() => {
                              const selectedTimeframe = timeframeOptions.find(option => option.id === formData.timeframeSelection);
                              const maxDays = selectedTimeframe?.maxDays || 365;
                              return moment(formData.startDate).add(maxDays - 1, 'days').format('YYYY-MM-DD');
                            })()}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="numberOfDays" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.numberOfDays')}
                          </label>
                          <input
                            type="number"
                            name="numberOfDays"
                            id="numberOfDays"
                            value={formData.numberOfDays}
                            onChange={handleChange}
                            min="1"
                            max={(() => {
                              const selectedTimeframe = timeframeOptions.find(option => option.id === formData.timeframeSelection);
                              return selectedTimeframe?.maxDays || 365;
                            })()}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            placeholder={`Enter 1-${(() => {
                              const selectedTimeframe = timeframeOptions.find(option => option.id === formData.timeframeSelection);
                              return selectedTimeframe?.maxDays || 365;
                            })()} days`}
                            required
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {t('mandatoryModal.maxDaysForTimeframe', { 
                              maxDays: (() => {
                                const selectedTimeframe = timeframeOptions.find(option => option.id === formData.timeframeSelection);
                                return selectedTimeframe?.maxDays || 365;
                              })()
                            })}
                          </p>
                        </div>

                        {/* Premium Calculation */}
                        <div className="col-span-6 sm:col-span-3">
                          <label className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.basePremium')}
                          </label>
                          <input
                            type="number"
                            value={formData.basePremium}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="limitOfIndemnity" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.limitOfIndemnity')}
                          </label>
                          <input
                            type="number"
                            name="limitOfIndemnity"
                            id="limitOfIndemnity"
                            value={0}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.netPremium', { percentage: formData.timeFramePercentage })}
                          </label>
                          <input
                            type="number"
                            value={formData.premium}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.taxPercent')}
                          </label>
                          <input
                            type="number"
                            value={formData.tax}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.supervisionFees')}
                          </label>
                          <input
                            type="number"
                            value={formData.supervisionFees}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.totalPremium')}
                          </label>
                          <input
                            type="number"
                            value={formData.totalPremium}
                            readOnly
                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 sm:text-sm font-bold"
                          />
                        </div>

                        {/* Standard Car Fields */}
                        <div className="col-span-6">
                          <h4 className="text-md font-medium text-gray-900 border-t pt-4">{t('mandatoryModal.vehicleInformation')}</h4>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="vehicleMake" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.vehicleMake')}
                          </label>
                          <select
                            name="vehicleMake"
                            id="vehicleMake"
                            value={formData.vehicleMake}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                            disabled={loadingMakes}
                          >
                            <option value="">
                              {loadingMakes ? t('mandatoryModal.loading') : t('mandatoryModal.selectMake')}
                            </option>
                            {carMakes.map((make) => (
                              <option key={make.id} value={make.name}>
                                {make.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="vehicleModel" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.vehicleModel')}
                          </label>
                          <select
                            name="vehicleModel"
                            id="vehicleModel"
                            value={formData.vehicleModel}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                            disabled={!formData.vehicleMake || loadingModels}
                          >
                            <option value="">
                              {loadingModels ? t('mandatoryModal.loading') : t('mandatoryModal.selectModel')}
                            </option>
                            {carModels.map((model) => (
                              <option key={model.id} value={model.name}>
                                {model.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="vehicleYear" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.vehicleYear')}
                          </label>
                          <select
                            name="vehicleYear"
                            id="vehicleYear"
                            value={formData.vehicleYear}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          >
                            <option value="">{t('mandatoryModal.selectYear')}</option>
                            {carYears.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="vehiclePlate" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.licensePlate')}
                          </label>
                          <input
                            type="text"
                            name="vehiclePlate"
                            id="vehiclePlate"
                            value={formData.vehiclePlate}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="carColour" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.carColour')}
                          </label>
                          <input
                            type="text"
                            name="carColour"
                            id="carColour"
                            value={formData.carColour}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        {/* Vehicle Horsepower field - Always shown for all mandatory car insurance */}
                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="vehicleHorsepower" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.vehicleHorsepower')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            name="vehicleHorsepower"
                            id="vehicleHorsepower"
                            value={formData.vehicleHorsepower}
                            onChange={handleChange}
                            min="1"
                            step="0.1"
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                            placeholder={t('mandatoryModal.enterVehicleHorsepower')}
                          />
                        </div>

                        {/* Show Passenger Count field for all personal vehicle options */}
                        {formData.mandatoryInsuranceType === 'personal_vehicle' && formData.mandatoryInsuranceSubType && (
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="passengerCount" className="block text-sm font-medium text-gray-700">
                              {t('mandatoryModal.passengerCount')}
                            </label>
                            <input
                              type="number"
                              name="passengerCount"
                              id="passengerCount"
                              value={formData.passengerCount}
                              onChange={handleChange}
                              min="1"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              {t('policy.additionalPerPassenger')}
                            </p>
                          </div>
                        )}

                        {/* Show Authorized Passengers field when needed */}
                        {(formData.mandatoryInsuranceType === 'public_passenger_car_city' ||
                          formData.mandatoryInsuranceType === 'public_car_outside_city' ||
                          formData.mandatoryInsuranceType === 'bus_transport') && (
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="authorizedPassengers" className="block text-sm font-medium text-gray-700">
                              {t('mandatoryModal.authorizedPassengers')}
                            </label>
                            <input
                              type="number"
                              name="authorizedPassengers"
                              id="authorizedPassengers"
                              value={formData.authorizedPassengers}
                              onChange={handleChange}
                              min="1"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            />
                          </div>
                        )}

                        {/* Vehicle Weight field - Always shown for all mandatory car insurance */}
                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="vehicleWeight" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.vehicleWeight')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            name="vehicleWeight"
                            id="vehicleWeight"
                            value={formData.vehicleWeight}
                            onChange={handleChange}
                            min="0"
                            step="0.1"
                            className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                              formData.mandatoryInsuranceType === 'personal_vehicle' ? 'bg-gray-50' : ''
                            }`}
                            required
                            readOnly={formData.mandatoryInsuranceType === 'personal_vehicle'}
                            placeholder={formData.mandatoryInsuranceType === 'personal_vehicle' ? 
                              t('mandatoryModal.automaticallySet') : 
                              t('mandatoryModal.enterVehicleWeight')
                            }
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {formData.mandatoryInsuranceType === 'personal_vehicle' ? 
                              t('mandatoryModal.personalVehicleWeightNote') : 
                              t('mandatoryModal.vehicleWeightNote')
                            }
                          </p>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="chassisNumber" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.chassisNumber')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="chassisNumber"
                            id="chassisNumber"
                            value={formData.chassisNumber}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                            placeholder={t('mandatoryModal.enterChassisNumber')}
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="designatedDriver" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.designatedDriver')}
                          </label>
                          <input
                            type="text"
                            name="designatedDriver"
                            id="designatedDriver"
                            value={formData.designatedDriver}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="designatedDriverLicense" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.driverLicenseNumber')}
                          </label>
                          <input
                            type="text"
                            name="designatedDriverLicense"
                            id="designatedDriverLicense"
                            value={formData.designatedDriverLicense}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        {/* Client Personal Information */}
                        <div className="col-span-6">
                          <h4 className="text-md font-medium text-gray-900 border-t pt-4">{t('mandatoryModal.clientInformation')}</h4>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.firstName')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            id="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="fatherName" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.fatherName')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="fatherName"
                            id="fatherName"
                            value={formData.fatherName}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="grandfatherName" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.grandfatherName')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="grandfatherName"
                            id="grandfatherName"
                            value={formData.grandfatherName}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.lastName')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            id="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.dateOfBirth')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            name="dateOfBirth"
                            id="dateOfBirth"
                            value={formData.dateOfBirth}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="nationality" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.nationality')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="nationality"
                            id="nationality"
                            value={formData.nationality}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="nationalId" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.nationalId')}
                          </label>
                          <input
                            type="text"
                            name="nationalId"
                            id="nationalId"
                            value={formData.nationalId}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="passportNumber" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.passportNumber')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="passportNumber"
                            id="passportNumber"
                            value={formData.passportNumber}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.licenseNumber')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="licenseNumber"
                            id="licenseNumber"
                            value={formData.licenseNumber}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.mobile')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="mobile"
                            id="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.email')}
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6">
                          <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.address')} <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            name="address"
                            id="address"
                            value={formData.address}
                            onChange={handleChange}
                            rows={2}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="occupation" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.occupation')}
                          </label>
                          <input
                            type="text"
                            name="occupation"
                            id="occupation"
                            value={formData.occupation}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="clientNumber" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.clientNumber')}
                          </label>
                          <input
                            type="text"
                            name="clientNumber"
                            id="clientNumber"
                            value={formData.clientNumber}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                        {/* Payment Information */}
                        <div className="col-span-6">
                          <h4 className="text-md font-medium text-gray-900 border-t pt-4">{t('mandatoryModal.paymentInformation')}</h4>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="paymentStatus" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.paymentStatus')}
                          </label>
                          <select
                            name="paymentStatus"
                            id="paymentStatus"
                            value={formData.paymentStatus}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            required
                          >
                            <option value="unpaid">{t('mandatoryModal.unpaid')}</option>
                            <option value="paid">{t('mandatoryModal.paid')}</option>
                          </select>
                        </div>

                        {formData.paymentStatus === 'paid' && (
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700">
                              {t('mandatoryModal.paymentMethod')}
                            </label>
                            <select
                              name="paymentMethod"
                              id="paymentMethod"
                              value={formData.paymentMethod}
                              onChange={handleChange}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              required
                            >
                              <option value="">{t('mandatoryModal.selectPaymentMethod')}</option>
                              <option value="cash">{t('mandatoryModal.cash')}</option>
                              <option value="cheque">{t('mandatoryModal.cheque')}</option>
                              <option value="on account">{t('mandatoryModal.onAccount')}</option>
                            </select>
                          </div>
                        )}

                        <div className="col-span-6">
                          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                            {t('mandatoryModal.notes')}
                          </label>
                          <textarea
                            name="notes"
                            id="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            rows={3}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          />
                        </div>

                      </div>
                      
                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-4 sm:gap-3">
                        {/* Save as Draft Button */}
                        <button
                          type="button"
                          onClick={handleSaveDraft}
                          className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          disabled={isSavingDraft || isSubmitting}
                        >
                          {isSavingDraft ? t('policy.saving') : t('policy.saveDraft')}
                        </button>

                        {/* Preview Button */}
                        <button
                          type="button"
                          onClick={handlePreview}
                          className="w-full inline-flex justify-center rounded-md border border-blue-300 shadow-sm px-4 py-2 bg-blue-50 text-base font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          disabled={isSavingDraft || isSubmitting}
                        >
                          {t('policy.preview')}
                        </button>

                        {/* Activate/Create Button (اصدار) */}
                        <button
                          type="button"
                          onClick={handleActivate}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                          disabled={!draftPolicy || formData.paymentStatus !== 'paid' || isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              {t('policy.activating')}
                            </>
                          ) : (
                            t('policy.activate')
                          )}
                        </button>

                        {/* Cancel Button */}
                        <button
                          type="button"
                          className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          onClick={handleClose}
                          disabled={isSubmitting || isSavingDraft}
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>

    {/* Document Viewer Modal for Preview */}
    {isPreviewOpen && draftPolicy && (
      <DocumentViewerModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        policyId={draftPolicy.id}
        documentType="html"
      />
    )}
    </>
  );
} 